import {
  ISource,
  ISourceStore,
  ISourceTPConfig,
  TPSettings,
  SecretConfig,
  SourceConfig,
} from '@stores/source';
import { action, computed, observable } from 'mobx';
import { IRootStore } from '@stores/index';
import { apiAuthCaller } from '@services/apiCaller';
import { getApiErrorMessage, isSourceNameUnique } from '@components/common/util/util';
import { IDestinationStore } from '@stores/destination';
import axios from 'axios';
import { SmartRetry } from '@utils/retry';
import { ISourceInfo } from '@components/sources/source/warehouseSource/configureData/tableMapping/tableMappingProvider';
import { IColumn } from '@components/sources/source/warehouseSource/configureData/sourceSpecificConfig';

import SourceFactory from '@components/sources/sourceFactory';
import {
  IResource,
  IResourceConfig,
} from '@components/sources/source/cloudSource/cloudSourceResources';
import { Resource } from '@components/sources/source/cloudSource/cloudSourceSettings';
import { IAccount } from '@components/sources/source/warehouseSource/auth/authAccounts';
import { CatchErr } from '@utils/types';
import { ISourceDefinition } from './sourceDefinitionsList';
import { ServerSourceTPConfig } from './sourcesList/types';
import { ApiResponse } from '@components/sources/visualizer/mappingScreens/sourceUtil';
import { SOURCE_CATEGORIES } from '@components/common/constants';

interface PreviewProps {
  rows: Array<Record<string, unknown>>;
  columns?: IColumn[];
  rowCount?: number;
}
interface CredentialsInfo {
  accounts: IAccount[];
  accountInfo: undefined | null | IAccount;
}

export interface ISourceDataStore extends ISourceStore {
  setSource(source: ISource): void;
  isEventStream(): boolean;
  getCredentialInfo(
    force?: boolean,
    useAccountId?: string,
    sourceDef?: ISourceDefinition,
  ): Promise<CredentialsInfo>;
  createAccount(accountData: unknown, sourceDef?: ISourceDefinition): Promise<IAccount>;
  deleteConnection(destination: IDestinationStore): void;
  getColumns<T = string>(sourceConfig?: ISourceInfo, sourceDefName?: string): Promise<IColumn<T>[]>;
  reset(): void;
  validateSource(config: SourceConfig): Promise<{ valid: boolean; reason: string }>;
  getPreview(
    accountId: string,
    sourceDefName: string,
    options: ISourceInfo,
    rowsLimit?: number,
    fetchColumns?: boolean,
  ): Promise<ApiResponse<PreviewProps>>;
  isSourceAvailableToSync(sourceId?: string): Promise<void>;
  getSourceDefResources(sourceDefName: string, params: unknown): Promise<IResource[] | null>;
  getSourceConfigResources(sourceId: string): Promise<IResourceConfig[]>;
  deleteCustomResource(sourceId: string, resourceId: string): void;
  deleteAccount(id: string): Promise<void>;
  unlinkTrackingPlan(): Promise<void>;
  updateTPSettings(config: TPSettings, onSuccess?: () => void): Promise<void>;
  updateName(val: string): void;
  enableSource(): void;
  disableSource(): void;
  toggleGatewayDumps(val: boolean): void;
}

export class SourceDataStore implements ISourceDataStore {
  @observable config: SourceConfig = {};

  @observable secretConfig: SecretConfig = {};

  @observable createdAt = '';

  @observable enabled = false;

  @observable id = '';

  @observable name = '';

  @observable sourceDefinitionId = '';

  @observable transient = false;

  @observable writeKey = '';

  @observable rootStore: IRootStore;

  @observable sourceDefAccounts = [];

  @observable accountInfo: undefined | null | IAccount = null;

  @observable trackingPlanConfig: ISourceTPConfig | undefined;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @action.bound
  setSource(source: ISource) {
    this.id = source.id;
    this.name = source.name;
    this.createdAt = source.createdAt;
    this.writeKey = source.writeKey;
    this.enabled = source.enabled;
    this.config = source.config;
    this.secretConfig = source.secretConfig;
    this.sourceDefinitionId = source.sourceDefinitionId;
    this.accountInfo = undefined;
    this.sourceDefAccounts = [];
    this.trackingPlanConfig = source.trackingPlanConfig;
    this.transient = source.transient;
  }

  @action.bound
  isEventStream() {
    return !this?.sourceDef?.category || this?.sourceDef?.category === 'webhook';
  }

  @action.bound
  reset() {
    this.config = {};
    this.secretConfig = {};
    this.createdAt = '';
    this.enabled = false;
    this.id = '';
    this.name = '';
    this.sourceDefinitionId = '';
    this.writeKey = '';
    this.sourceDefAccounts = [];
    this.accountInfo = null;
  }

  @action.bound
  public setName(name: string): void {
    this.name = name;
  }

  @computed get sourceDef() {
    return this.rootStore.sourceDefinitionsListStore.getSourceDef(this.sourceDefinitionId)!;
  }

  @computed get destinations() {
    const destIds = this.rootStore.connectionsStore.connections[this.id] || [];
    return this.rootStore.destinationsListStore.destinations.filter((dest) =>
      destIds.includes(dest.id),
    );
  }

  @computed get category() {
    return this.sourceDef.category;
  }

  validateName = (name: string) => {
    const { sourcesListStore } = this.rootStore;
    if (!name) {
      throw { message: 'Name cannot be empty' };
    }

    if (!isSourceNameUnique(name, sourcesListStore!.sources)) {
      throw { message: 'Please choose a unique source name' };
    }
    return true;
  };

  updateSourceInList() {
    const { sourcesListStore } = this.rootStore;
    sourcesListStore.updateSourceInList(this);
  }

  /**
   * updateName
   */

  @action.bound
  public async updateName(name: string) {
    name = name && name.trim();
    if (name === this.name) {
      return true;
    }
    const {
      messagesStore,
      userStore: { token },
    } = this.rootStore;

    try {
      this.validateName(name);
    } catch (e) {
      messagesStore.showErrorMessage('A source already exists with the same name.');
      throw e;
    }

    try {
      await apiAuthCaller(token).patch(`/sources/${this.id}`, { name });
      this.name = name;
      this.updateSourceInList();
      messagesStore.showSuccessMessage('Name updated successfully');
      this.track('source name updated', { updatedSourceName: this.name });
      return true;
    } catch (error) {
      messagesStore.showErrorMessage('Unable to update name. Please try again.');
      this.track('source name update failure', { error });
      throw error;
    }
  }

  @action.bound
  public async updateConfig(config: SourceConfig, hardUpdate = false) {
    const {
      messagesStore,
      userStore: { token },
    } = this.rootStore;
    try {
      const {
        accountInfo,
        customResources,
        credentials,
        accessToken,
        refreshToken,
        rudderAccountId,
        accountId,
        ...configToUpdate
      } = this.config;
      const updatedConfig = {
        ...configToUpdate,
        accountId: rudderAccountId || accountId,
        ...config,
      };
      if (updatedConfig.resources) {
        updatedConfig.resources = (updatedConfig.resources || []).map((resource: Resource) => {
          if (
            typeof resource === 'string' ||
            (typeof resource === 'object' && this.sourceDef.category === SOURCE_CATEGORIES.SINGER)
          ) {
            return resource;
          }
          return resource?.id;
        });
      }
      const updateUrl = `/sources/${this.id}${hardUpdate ? '?hardUpdate=true' : ''}`;
      const res = await apiAuthCaller(token).post(updateUrl, {
        config: updatedConfig,
      });
      this.config = res.data.config;
      this.secretConfig = res.data.secretConfig;
      this.updateSourceInList();
      messagesStore.showSuccessMessage('Successfully updated');
      this.track('configuration updated');
      return res.status === 200;
    } catch (error) {
      messagesStore.showErrorMessage(getApiErrorMessage(error, 'Failed to update source'));
      this.track('configuration update failed');
      return false;
    }
  }

  /**
   * Enable Source
   */
  @action.bound
  public async enableSource() {
    try {
      const { token } = this.rootStore.userStore;
      await apiAuthCaller(token).post(`/sources/${this.id}`, {
        enabled: true,
      });
      this.enabled = true;
      this.updateSourceInList();
      // this.trackConnectionToggle(BI_EVENTS.CONNECTION_ENABLED);
    } catch (error) {}
  }

  /**
   * Disable Source
   */
  @action.bound
  public async disableSource() {
    try {
      const { token } = this.rootStore.userStore;
      await apiAuthCaller(token).post(`/sources/${this.id}`, {
        enabled: false,
      });
      this.enabled = false;
      this.updateSourceInList();
      // this.trackConnectionToggle(BI_EVENTS.CONNECTION_DISABLED);
    } catch (error) {}
  }

  // private trackConnectionToggle(message: string) {
  //   analytics.track2(message, TRACK_TYPE.API_SUCCESS, {
  //     source_name: this.name,
  //     source_type: this.sourceDef.displayName,
  //     source_category: prettyPrintSourceCategory(this.sourceDef),
  //     location: 'source',
  //   });
  // }

  @action.bound
  public async getCredentialInfo(force = false) {
    if (this.sourceDefAccounts.length === 0 || force) {
      try {
        this.sourceDefAccounts = [];
        this.accountInfo = undefined;
        const { token } = this.rootStore.userStore;
        const url = SourceFactory(this.sourceDef.category).getAccountsEndpoint(this.sourceDef.name);
        const accounts = await apiAuthCaller(token).get(url);
        this.sourceDefAccounts = accounts.data;
        const { rudderAccountId, accountId } = this.config;
        this.accountInfo =
          rudderAccountId || accountId
            ? this.sourceDefAccounts.find(
                (currAcc: IAccount) => currAcc.id === rudderAccountId || currAcc.id === accountId,
              )
            : null;
      } catch (e) {}
    }
    return { accountInfo: this.accountInfo, accounts: this.sourceDefAccounts };
  }

  isWarehouse() {
    return SourceFactory(this.sourceDef.category).isWarehouse();
  }

  async createAccount(accountData: unknown, sourceDef?: ISourceDefinition) {
    const { userStore, messagesStore } = this.rootStore;
    try {
      const url = SourceFactory(
        sourceDef?.category || this.sourceDef.category,
      ).getAccountsEndpoint();
      const response = await apiAuthCaller(userStore.token).post(url, accountData);
      return response.data;
    } catch (err: CatchErr) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to save credentials'));
      throw err;
    }
  }

  async deleteConnection(destination: IDestinationStore) {
    const { connectionsStore } = this.rootStore;
    await connectionsStore.removeConnections(this, destination);
  }

  async getColumns<T = string>(
    sourceConfig?: ISourceInfo,
    sourceDefName?: string,
  ): Promise<IColumn<T>[]> {
    const { userStore } = this.rootStore;
    const retry = new SmartRetry({ times: 1 });
    const timeout = 120000;
    let req;
    if (sourceConfig?.sql || sourceConfig?.sqlModelId) {
      req = { sql: sourceConfig?.sql, sqlModelId: sourceConfig?.sqlModelId };
    } else {
      req = {
        schema: sourceConfig?.schema || this.config?.query?.table?.schema,
        table: sourceConfig?.table || this.config?.query?.table?.name,
        bucketName: sourceConfig?.bucketName || this.config?.bucketName,
        prefix: sourceConfig?.prefix || this.config?.prefix,
      };
    }
    const response = await apiAuthCaller(userStore.token, false, timeout).post(
      `cloudSources/sources/info/${sourceDefName || this.sourceDef.name}/columns`,
      {
        accountId: sourceConfig?.accountId || this.config.accountId || this.config.rudderAccountId,
        ...req,
      },
      {
        cancelToken: new axios.CancelToken((c) => {
          retry.setCancelMethod(c);
        }),
      },
    );
    return response?.data?.columns || [];
  }

  validateSource = async (
    config: SourceConfig,
  ): Promise<{
    valid: boolean;
    reason: string;
  }> => {
    const { userStore } = this.rootStore;
    return SourceFactory(this.sourceDef.category).validateSource(userStore.token, config, this);
  };

  public track(event: string, info: Record<string, unknown> = {}) {
    window.rudderanalytics.track(event, {
      sourceID: this.id,
      sourceType: this.sourceDef.displayName,
      ...info,
    });
  }

  async getPreview(
    accountId: string,
    sourceDefName: string,
    options: ISourceInfo,
    rowLimit?: number,
    fetchColumns?: boolean,
  ): Promise<ApiResponse<PreviewProps>> {
    const {
      userStore: { token },
    } = this.rootStore;
    let req;
    if (options.filterSpec) {
      req = {
        filterSpec: options.filterSpec,
        fetchRowCount: true,
      };
    } else if (options?.sql || options?.sqlModelId) {
      req = { sql: options?.sql, sqlModelId: options?.sqlModelId };
    } else {
      req = {
        schema: options?.schema,
        table: options?.table,
        bucketName: options?.bucketName,
        prefix: options?.prefix,
      };
    }
    const response = await apiAuthCaller(token).post(
      `cloudSources/sources/info/${sourceDefName || this.sourceDef.name}/preview`,
      { accountId, rowLimit, fetchColumns, ...req },
    );
    return response.data;
  }

  public isSourceAvailableToSync = async (sourceId?: string): Promise<void> => {
    const {
      userStore: { token },
    } = this.rootStore;
    await apiAuthCaller(token).get(`/cloudSources/sources/${sourceId || this.id}/status`);
  };

  public getSourceDefResources = async (sourceDefName: string, params: unknown) => {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    try {
      const resp = await apiAuthCaller(token).post(
        `/cloudSources/info/roles/${sourceDefName}/resources`,
        params,
      );
      return resp.data;
    } catch (error: CatchErr) {
      messagesStore.showErrorMessage(getApiErrorMessage(error, 'Failed to create roles resource'));
      return null;
    }
  };

  public getSourceConfigResources = async (sourceId: string) => {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    try {
      const resp = await apiAuthCaller(token).get(`/cloudSources/source/${sourceId}/resources`);
      return resp.data;
    } catch (error: CatchErr) {
      messagesStore.showErrorMessage(getApiErrorMessage(error, 'Failed to get source resource'));
      return null;
    }
  };

  public deleteCustomResource = async (sourceId: string, resourceId: string) => {
    const {
      userStore: { token },
    } = this.rootStore;
    await apiAuthCaller(token).delete(`/cloudSources/source/${sourceId}/resources/${resourceId}`);
  };

  deleteAccount = async (id: string) => {
    const {
      userStore: { token },
    } = this.rootStore;
    await apiAuthCaller(token).delete(`/warehouseSources/accounts/${id}`);
  };

  toggleGatewayDumps = (val: boolean) => {
    const {
      userStore,
      messagesStore: { showErrorMessage },
    } = this.rootStore;
    return apiAuthCaller(userStore.token)
      .patch(`/sources/${this.id}`, { transient: val })
      .then(() => {
        this.transient = val;
      })
      .catch(() => showErrorMessage('Failed to update source settings'));
  };

  @action.bound
  async unlinkTrackingPlan() {
    const { trackingPlanListStore } = this.rootStore;
    await trackingPlanListStore.unlinkTrackingPlan(
      this.trackingPlanConfig?.trackingPlanId!,
      this.id,
      () => {
        this.trackingPlanConfig = undefined;
      },
    );
  }

  @action.bound
  async updateTPSettings(config: TPSettings, onSuccess?: () => void) {
    const {
      userStore: { token },
      messagesStore,
    } = this.rootStore;
    const {
      dropUnplannedEvents,
      dropUnplannedProperties,
      dropOtherViolation,
      propagateValidationErrors,
    } = config;
    try {
      const updatedConfig: { track: ServerSourceTPConfig['track'] } = {
        track: {
          sendViolatedEventsTo: this.trackingPlanConfig!.config.track.sendViolatedEventsTo,
          propagateValidationErrors: propagateValidationErrors ? 'true' : 'false',
          allowUnplannedEvents: dropUnplannedEvents ? 'false' : 'true',
          unplannedProperties: dropUnplannedProperties ? 'drop' : 'forward',
          anyOtherViolation: dropOtherViolation ? 'drop' : 'forward',
        },
      };
      await apiAuthCaller(token).put(`/trackingPlans/sources/${this.id}`, {
        config: updatedConfig,
      });
      this.trackingPlanConfig!.config.track = {
        ...this.trackingPlanConfig?.config.track,
        ...config,
      };
      onSuccess?.();
    } catch (error: CatchErr) {
      messagesStore.showErrorMessage(
        getApiErrorMessage(error, 'Failed to update tracking plan settings'),
      );
    }
  }
}
